import { NavLink, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  useUser,
  useClerk,
  Show,
} from "@clerk/react";
import { LogOut, Sparkles, Plus, TrendingUp, Trash2 } from "lucide-react";
import { AuthModal } from "@/components/ui/auth-modal";
import { DeleteAccountModal } from "@/components/ui/delete-account-modal";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { navItems, creationNavItems, utilityNavItems, type NavItem } from "@/config/navigation";
import { useUserStore } from "@/stores/user-store";

export function Sidebar() {
  return (
    <aside className="w-[240px] h-full bg-[#faf6ed]/40 backdrop-blur-xl flex flex-col overflow-hidden border-r border-[rgba(45,49,39,0.06)]">
      <div className="px-3 pt-4 pb-2">
        <NavLink
          to="/create/text-to-image"
          className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[13px] font-bold hover:from-[#e8822e] hover:to-[#d4712a] active:from-[#d4712a] active:to-[#c06020] transition-all duration-200 shadow-sm shadow-orange-500/20 hover:shadow-md hover:shadow-orange-500/25 hover:-translate-y-px"
        >
          <Plus className="size-4" strokeWidth={2.5} />
          New Creation
        </NavLink>
      </div>

      <div className="flex-1 min-h-0 pt-2 pb-3 overflow-y-auto overscroll-none no-scrollbar">
        <SidebarNav />
      </div>

      <div className="shrink-0 px-3 pb-2">
        <TrendingConcepts />
      </div>

      <div className="shrink-0">
        <ProfileSection />
      </div>
    </aside>
  );
}

function SidebarNav() {
  const location = useLocation();

  const allItems = [...navItems, ...creationNavItems, ...utilityNavItems];
  const activeHref = allItems.find((item) =>
    item.href === "/" ? location.pathname === "/" : location.pathname.startsWith(item.href),
  )?.href;

  return (
    <nav className="px-3">
      <div className="space-y-0.5">
        {navItems.map((item) => (
          <SidebarLink key={item.href} item={item} isActive={activeHref === item.href} />
        ))}
      </div>

      <div className="my-3 mx-2 h-px bg-[rgba(45,49,39,0.04)]" />

      <SectionLabel>Create</SectionLabel>
      <div className="space-y-0.5">
        {creationNavItems.map((item) => (
          <SidebarLink key={item.href} item={item} isActive={activeHref === item.href} />
        ))}
      </div>

      <div className="my-3 mx-2 h-px bg-[rgba(45,49,39,0.04)]" />

      <SectionLabel>Account</SectionLabel>
      <div className="space-y-0.5">
        {utilityNavItems.map((item) => (
          <SidebarLink
            key={item.href}
            item={item}
            isActive={activeHref === item.href}
            highlight={item.href === "/billing"}
          />
        ))}
      </div>
    </nav>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 text-[10px] font-bold text-[#b5b0a0] uppercase tracking-[0.16em]">
      {children}
    </div>
  );
}

function SidebarLink({ item, isActive, highlight }: { item: NavItem; isActive: boolean; highlight?: boolean }) {
  const Icon = item.icon;
  const { user } = useUserStore();
  const hasPurchased = user?.highest_package != null;
  const shouldHighlight = highlight && !hasPurchased && !isActive;

  return (
    <NavLink
      to={item.href}
      end={item.href === "/"}
      className={cn(
        "relative flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 group",
        isActive
          ? "text-[#2d3127] bg-white/70 shadow-sm border border-white/60"
          : shouldHighlight
            ? "text-amber-700 bg-amber-50/60 border border-amber-200/40 hover:bg-amber-100/60"
            : "text-[#8a8570] hover:text-[#5a5545] hover:bg-white/40",
      )}
    >
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-gradient-to-b from-[#f0923a] to-[#e8822e]" />
      )}
      <Icon
        className={cn(
          "size-[16px] transition-all duration-200",
          isActive ? "text-[#e8822e]" : shouldHighlight ? "text-amber-600" : "text-[#b5b0a0] group-hover:text-[#8a8570]",
        )}
        strokeWidth={isActive ? 2 : 1.8}
      />
      <span className="flex-1">{item.label}</span>
    </NavLink>
  );
}


const TRENDING_CONCEPTS = [
  "Dreamcore landscapes",
  "Brutalist architecture",
  "Solarpunk cities",
  "Dark academia aesthetic",
  "Bioluminescent forests",
  "Retrofuturism",
  "Liminal spaces",
  "Ethereal portraits",
  "Cyberpunk street markets",
  "Cottagecore interiors",
  "Cosmic horror",
  "Art Nouveau posters",
  "Vaporwave sunsets",
  "Steampunk machinery",
  "Ukiyo-e waves",
  "Glitchcore textures",
  "Baroque still life",
  "Afrofuturism",
  "Miniature worlds",
  "Neon noir alleyways",
];

function TrendingConcepts() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * TRENDING_CONCEPTS.length));
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % TRENDING_CONCEPTS.length);
        setFade(true);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const concept = TRENDING_CONCEPTS[index];

  return (
    <Link
      to={`/create/text-to-image?prompt=${encodeURIComponent(concept)}`}
      className="block rounded-xl bg-white/40 backdrop-blur-sm border border-white/50 p-3 hover:bg-white/60 hover:border-white/70 transition-all duration-200 group hover:shadow-sm"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <TrendingUp className="size-3 text-emerald-500/70" />
        <span className="text-[9px] font-bold text-[#b5b0a0] uppercase tracking-wider">Trending today</span>
      </div>
      <p
        className={cn(
          "text-[12px] font-medium text-[#5a5545] group-hover:text-[#2d3127] transition-all duration-300 truncate",
          fade ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1",
        )}
      >
        {concept}
      </p>
      <p className="text-[9px] text-[#c5c0b0] mt-1 leading-snug">
        Users are creating with this theme
      </p>
    </Link>
  );
}

function ProfileSection() {
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/users/${user.id}`);
      await signOut();
    } catch {
      setIsDeleting(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="p-3">
        <div className="h-12 rounded-xl bg-[rgba(45,49,39,0.03)] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-3 border-t border-[rgba(45,49,39,0.04)]">
      <Show when="signed-in">
        <div className="flex items-center gap-2.5 px-1 py-1">
          <div className="relative">
            <img
              src={user?.imageUrl}
              alt={user?.fullName || "Profile"}
              className="size-9 rounded-xl ring-1 ring-white/60 shadow-sm"
            />
            <div className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-500 border-2 border-[#faf6ed]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-[#2d3127] truncate">
              {user?.fullName || user?.username}
            </p>
            <p className="text-[10px] text-[#b5b0a0] truncate">
              {user?.primaryEmailAddress?.emailAddress}
            </p>
          </div>
        </div>
        <button
          onClick={() => signOut()}
          className="flex items-center gap-2.5 w-full mt-2 px-3 py-2 rounded-xl text-[12px] font-medium text-[#8a8570] hover:text-red-500 hover:bg-red-50/60 transition-all duration-200"
        >
          <LogOut className="size-3.5" strokeWidth={1.8} />
          Sign out
        </button>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-[12px] font-medium text-[#8a8570] hover:text-red-500 hover:bg-red-50/60 transition-all duration-200"
        >
          <Trash2 className="size-3.5" strokeWidth={1.8} />
          Delete Account
        </button>
        <DeleteAccountModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteAccount}
          isDeleting={isDeleting}
        />
      </Show>
      <Show when="signed-out">
        <SidebarAuth />
      </Show>
    </div>
  );
}

function SidebarAuth() {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <div>
      <button
        onClick={() => setShowAuth(true)}
        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[12px] font-bold hover:from-[#e8822e] hover:to-[#d4712a] transition-all duration-200 shadow-sm shadow-orange-500/20"
      >
        <Sparkles className="size-3.5" />
        Get Started Free
      </button>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </div>
  );
}
